import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { API_URL } from '@/config';
import { useNotificationStore } from '@/stores/notifications';
import storage from '@/utils/storage';
import { versionLabel } from '@/version';

const CLIENT_BUILD = versionLabel.replace(/[^\x20-\x7E]/g, '-').slice(0, 128);

function authRequestInterceptor(config: AxiosRequestConfig) {
  const token = storage.getToken();
  if (token) {
    config.headers.authorization = `${token}`;
  }
  config.headers.Accept = 'application/json';
  config.headers['X-Client-Build'] = CLIENT_BUILD;
  return config;
}

function unwrapResponse(response: AxiosResponse) {
  return response.data;
}

function notifyError(error: any) {
  const message = error.response?.data?.message || error.message;
  useNotificationStore.getState().addNotification({
    type: 'error',
    title: 'Error',
    message,
  });

  return Promise.reject(error);
}

export const axios = Axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axios.interceptors.request.use(authRequestInterceptor);
axios.interceptors.response.use(unwrapResponse, notifyError);

/**
 * Client for URLs outside our own API (Lambda function URLs, presigned S3 links).
 *
 * It deliberately skips the auth/X-Client-Build request interceptor: those
 * headers turn a plain GET into a preflighted CORS request, and the external
 * endpoints don't answer OPTIONS. Responses are still unwrapped to `data` and
 * errors still surface as notifications, matching `axios` above.
 */
export const externalAxios = Axios.create();

externalAxios.interceptors.response.use(unwrapResponse, notifyError);
